import React, { useEffect, useState, useRef } from "react";
import {
  getDatabase,
  ref,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  update,
  query,
  orderByChild,
  limitToFirst,
  get,
} from "firebase/database";
import { db } from "./firebase/config";
import { ClipLoader } from "react-spinners";
import { FiXCircle } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Component con cho mỗi sản phẩm có thể sắp xếp
const SortableProductItem = ({
  product,
  index,
  isSortMode,
  onProductClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.8 : 1,
    boxShadow: isDragging
      ? "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)"
      : "",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(isSortMode ? { ...attributes, ...listeners } : {})}
      className={`bg-white rounded-lg shadow-md p-4 relative transform transition duration-200 ease-in-out 
        ${
          isSortMode
            ? isDragging
              ? "border-2 border-blue-500"
              : "hover:border-2 hover:border-blue-300 cursor-grab"
            : "hover:scale-105 hover:shadow-lg cursor-pointer"
        }`}
      onClick={() => onProductClick(product)}
    >
      {isSortMode && (
        <div className="absolute top-2 left-2 bg-blue-500 text-white px-2 py-1 rounded-full text-xs flex items-center">
          <span className="mr-2">{index + 1}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </div>
      )}
      <h2 className="text-2xl font-bold text-gray-800 mb-2 truncate whitespace-nowrap overflow-hidden">
        {product.name}
      </h2>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {Array.isArray(product.images) &&
          product.images
            .slice(0, 2)
            .map((image, imgIndex) => (
              <img
                key={imgIndex}
                src={image}
                alt={`Product ${product.id} Image ${imgIndex}`}
                className="w-full h-40 object-contain bg-gray-100"
              />
            ))}
      </div>
      {product.fanpages && product.fanpages.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-2">
            {product.fanpages.map((page, pageIndex) => (
              <span
                key={pageIndex}
                className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded"
              >
                {page.name}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {product.images?.length || 0} ảnh
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              product.isActive
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {product.isActive ? "🔵 Đang chạy" : "⚪ Đã tắt"}
          </div>
        </div>
      </div>
      {product.isPosted && (
        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
          Đã đăng
        </div>
      )}
      {isSortMode && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200 rounded-lg">
          <div className="hidden group-hover:block text-white font-bold">
            Kéo để sắp xếp
          </div>
        </div>
      )}
    </div>
  );
};

// Component hiển thị sản phẩm dạng danh sách cho chế độ sắp xếp
const SortableProductListItem = ({ product, index, onProductClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: product.id,
    transition: {
      duration: 150,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.8 : 1,
    backgroundColor: isDragging ? "#f3f4f6" : "white",
    touchAction: "none",
    maxWidth: "100%",
  };

  // Tăng độ dài hiển thị cho tên sản phẩm
  const truncateName = (name) => {
    if (name.length > 60) {
      return name.substring(0, 58) + "...";
    }
    return name;
  };

  // Tăng độ dài hiển thị cho tên fanpage
  const truncateFanpageName = (name) => {
    if (name.length > 35) {
      return name.substring(0, 33) + "...";
    }
    return name;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-start p-2 border rounded-lg ${
        isDragging
          ? "shadow-lg border-blue-400"
          : "border-gray-200 hover:border-blue-300"
      } transition-all duration-200 touch-manipulation w-full overflow-hidden`}
    >
      {/* Số thứ tự */}
      <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 text-blue-800 rounded-full mr-2 flex-shrink-0 touch-manipulation mt-0.5">
        <span className="text-xs font-medium">{index + 1}</span>
      </div>

      {/* Thông tin sản phẩm */}
      <div className="flex-grow min-w-0 mr-2 overflow-hidden">
        <div className="font-medium text-gray-800 text-xs sm:text-sm leading-tight mb-0.5">
          {truncateName(product.name)}
        </div>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {product.fanpages &&
            product.fanpages.length > 0 &&
            product.fanpages.slice(0, 1).map((page, pageIndex) => (
              <span
                key={pageIndex}
                className="bg-blue-50 text-blue-700 text-xs px-1.5 py-0.5 rounded-sm truncate max-w-[200px] sm:max-w-[250px] leading-tight"
              >
                {truncateFanpageName(page.name)}
              </span>
            ))}
          {product.fanpages && product.fanpages.length > 1 && (
            <span className="text-xs text-gray-500 leading-tight">
              +{product.fanpages.length - 1}
            </span>
          )}
        </div>
      </div>

      {/* Trạng thái và nút kéo */}
      <div className="flex items-center flex-shrink-0 ml-auto">
        <div
          className={`px-1.5 py-0.5 rounded text-xs ${
            product.isActive
              ? "bg-blue-100 text-blue-800"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {product.isActive ? "On" : "Off"}
        </div>

        <div className="ml-1.5 text-gray-400 cursor-grab bg-gray-100 p-1 rounded">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

// Thêm component SortableGridItem cho chế độ grid
const SortableGridItem = ({ product, index, onProductClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: product.id,
    transition: {
      duration: 150,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 1,
    opacity: isDragging ? 0.8 : 1,
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative bg-white rounded-lg shadow-md p-4 ${
        isDragging ? "shadow-xl border-2 border-blue-500" : ""
      }`}
    >
      {/* Thêm div bao bọc để xử lý sự kiện kéo thả */}
      <div
        {...attributes}
        {...listeners}
        className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing touch-manipulation rounded-lg"
        style={{ touchAction: "none" }}
      >
        {/* Vùng trong suốt để kéo thả */}
        <div
          className="absolute inset-0"
          style={{ pointerEvents: "none" }}
        ></div>
      </div>

      {/* Hiển thị chỉ báo kéo thả */}
      <div className="absolute top-0 left-0 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-br-md z-20 flex items-center">
        <span>{index + 1}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3 ml-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16m-7 6h7"
          />
        </svg>
      </div>

      <h2 className="text-xl font-bold text-gray-800 mb-2 truncate whitespace-nowrap overflow-hidden">
        {product.name}
      </h2>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {Array.isArray(product.images) &&
          product.images
            .slice(0, 2)
            .map((image, imgIndex) => (
              <img
                key={imgIndex}
                src={image}
                alt={`Product ${product.id} Image ${imgIndex}`}
                className="w-full h-32 object-contain bg-gray-100"
              />
            ))}
      </div>
      {product.fanpages && product.fanpages.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-2">
            {product.fanpages.map((page, pageIndex) => (
              <span
                key={pageIndex}
                className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
              >
                {page.name}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {product.images?.length || 0} ảnh
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              product.isActive
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {product.isActive ? "🔵 Đang chạy" : "⚪ Đã tắt"}
          </div>
        </div>
      </div>
      {product.isPosted && (
        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
          Đã đăng
        </div>
      )}
    </div>
  );
};

const ProductsScreen = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFanpage, setSelectedFanpage] = useState("all");
  const [fanpages, setFanpages] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState("all");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSortMode, setIsSortMode] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // "list" hoặc "grid"
  const [visibleCount, setVisibleCount] = useState(50);

  // Cấu hình sensors cho DndContext
  const sensors = useSensors(
    useSensor(TouchSensor, {
      // Tăng ngưỡng kích hoạt để tránh kích hoạt vô tình
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(PointerSensor, {
      // Cấu hình cho chuột và bút stylus
      activationConstraint: {
        distance: 8, // Khoảng cách tối thiểu để kích hoạt kéo
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const productsRef = query(
      ref(db, `products/${userId}`),
      orderByChild("position"),
      limitToFirst(visibleCount)
    );

    const productMap = new Map();
    const fanpagesMap = new Map();
    let isInitialLoad = true;

    // OPTIMIZED: Use child listeners instead of onValue to avoid downloading entire node on each change
    // This reduces bandwidth: only changed product is downloaded, not all products
    const unsubscribeAdded = onChildAdded(productsRef, (snapshot) => {
      const id = snapshot.key;
      const product = { ...snapshot.val(), id, position: snapshot.val().position || 9999 };
      productMap.set(id, product);

      // Extract fanpages from this product
      if (product.fanpages && Array.isArray(product.fanpages)) {
        product.fanpages.forEach((page) => {
          if (page && page.id && !fanpagesMap.has(page.id)) {
            fanpagesMap.set(page.id, {
              id: page.id,
              name: page.name,
              access_token: page.access_token,
            });
          }
        });
      }

      // Update state only after initial load is complete
      if (isInitialLoad) {
        setIsLoading(false);
        isInitialLoad = false;
      }
      updateProductsState();
    });

    const unsubscribeChanged = onChildChanged(productsRef, (snapshot) => {
      const id = snapshot.key;
      const product = { ...snapshot.val(), id, position: snapshot.val().position || 9999 };
      productMap.set(id, product);

      // Extract fanpages from this product
      if (product.fanpages && Array.isArray(product.fanpages)) {
        product.fanpages.forEach((page) => {
          if (page && page.id && !fanpagesMap.has(page.id)) {
            fanpagesMap.set(page.id, {
              id: page.id,
              name: page.name,
              access_token: page.access_token,
            });
          }
        });
      }

      updateProductsState();
    });

    const unsubscribeRemoved = onChildRemoved(productsRef, (snapshot) => {
      const id = snapshot.key;
      productMap.delete(id);
      updateProductsState();
    });

    const updateProductsState = () => {
      const productList = Array.from(productMap.values());
      productList.sort((a, b) => a.position - b.position);
      setProducts(productList);
      setFanpages(Array.from(fanpagesMap.values()));
    };

    return () => {
      unsubscribeAdded();
      unsubscribeChanged();
      unsubscribeRemoved();
    };
  }, [visibleCount]);

  // Sửa lại hàm handleDragEnd để sử dụng số nguyên dương cho position
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = searchAndStatusFilteredProducts.findIndex(
      (product) => product.id === active.id
    );
    const newIndex = searchAndStatusFilteredProducts.findIndex(
      (product) => product.id === over.id
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      // Tạo bản sao của danh sách sản phẩm đã lọc
      const newFilteredItems = [...searchAndStatusFilteredProducts];

      // Lấy sản phẩm đang được kéo
      const [movedItem] = newFilteredItems.splice(oldIndex, 1);

      // Chèn sản phẩm vào vị trí mới
      newFilteredItems.splice(newIndex, 0, movedItem);

      // Cập nhật vị trí cho tất cả sản phẩm trong danh sách đã lọc
      // Sử dụng số nguyên dương bắt đầu từ 1
      const updatedPositions = {};
      newFilteredItems.forEach((item, index) => {
        updatedPositions[item.id] = index + 1; // Vị trí bắt đầu từ 1
      });

      // Cập nhật state
      const newProducts = products.map((product) => {
        if (updatedPositions.hasOwnProperty(product.id)) {
          return {
            ...product,
            position: updatedPositions[product.id],
          };
        }
        return product;
      });

      // Sắp xếp lại danh sách sản phẩm theo position
      newProducts.sort((a, b) => a.position - b.position);

      setProducts(newProducts);

      // Hiển thị thông báo nhỏ khi thay đổi vị trí
      // toast.info(
      //   `Đã di chuyển "${movedItem.name}" đến vị trí ${newIndex + 1}`,
      //   {
      //     autoClose: 1000,
      //     hideProgressBar: true,
      //     position: "bottom-center",
      //   }
      // );

      // Lưu thứ tự mới vào Firebase
      const userId = localStorage.getItem("userId");
      const updates = {};

      // Cập nhật vị trí cho tất cả sản phẩm bị ảnh hưởng
      Object.keys(updatedPositions).forEach((productId) => {
        updates[`products/${userId}/${productId}/position`] =
          updatedPositions[productId];
      });

      update(ref(db), updates)
        .then(() => {
          // Không hiển thị thông báo ở đây để tránh quá nhiều thông báo
        })
        .catch((error) => {
          toast.error("Lỗi khi lưu thứ tự: " + error.message);
        });
    }
  };

  const handleFanpageFilter = (fanpageId) => {
    setSelectedFanpage(fanpageId);
    setIsDropdownOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredProducts =
    selectedFanpage === "all"
      ? products
      : products.filter((product) =>
          product.fanpages?.some((page) => page.id === selectedFanpage)
        );

  const searchAndStatusFilteredProducts = filteredProducts.filter((product) => {
    const matchesSearch =
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.fanpages?.some((page) =>
        page.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesStatus =
      filterStatus === "all"
        ? true
        : filterStatus === "active"
        ? product.isActive
        : !product.isActive;

    return matchesSearch && matchesStatus;
  });

  const handleProductClick = (product) => {
    if (isSortMode) return;

    navigate("/product-create", {
      state: { product },
    });
  };

  const toggleSortMode = () => {
    // if (isSortMode) {
    //   // Khi thoát khỏi chế độ sắp xếp, cập nhật vị trí cho tất cả sản phẩm
    //   updateAllPositions();
    //   // Trở về chế độ hiển thị trước đó
    //   setViewMode("list");
    // } else {
    //   // Khi bật chế độ sắp xếp, chuyển sang chế độ grid
    //   setViewMode("grid");
    // }
    setIsSortMode(!isSortMode);
  };

  // Thêm hàm để cập nhật vị trí cho tất cả sản phẩm
  const updateAllPositions = () => {
    // Sắp xếp tất cả sản phẩm theo vị trí hiện tại
    const sortedProducts = [...products].sort(
      (a, b) => a.position - b.position
    );

    // Cập nhật vị trí mới bắt đầu từ 1
    const updates = {};
    const userId = localStorage.getItem("userId");

    sortedProducts.forEach((product, index) => {
      updates[`products/${userId}/${product.id}/position`] = index + 1;
    });

    // Cập nhật state
    const newProducts = sortedProducts.map((product, index) => ({
      ...product,
      position: index + 1,
    }));

    setProducts(newProducts);

    // Lưu vào Firebase
    update(ref(db), updates)
      .then(() => {
        toast.success("Đã cập nhật vị trí cho tất cả sản phẩm");
      })
      .catch((error) => {
        toast.error("Lỗi khi cập nhật vị trí: " + error.message);
      });
  };

  // Cập nhật CSS cho thiết bị di động
  useEffect(() => {
    if (isSortMode) {
      document.body.classList.add("sorting-mode");

      const style = document.createElement("style");
      style.id = "drag-cursor-style";
      style.innerHTML = `
        .sorting-mode [data-sortable="true"] {
          cursor: grab !important;
        }
        .sorting-mode [data-sortable="true"]:active {
          cursor: grabbing !important;
        }
        
        /* Styles cho thiết bị di động */
        @media (max-width: 768px) {
          .sorting-mode .flex-col.space-y-2 > div,
          .sorting-mode .flex-col.space-y-1 > div {
            padding: 4px 6px;
            margin-bottom: 3px;
            max-width: 100%;
            width: 100%;
            box-sizing: border-box;
          }
          
          .sorting-mode .flex-col.space-y-2 > div:active,
          .sorting-mode .flex-col.space-y-1 > div:active {
            background-color: #f3f4f6;
          }
          
          /* Giảm padding cho container */
          .sorting-mode .bg-white.rounded-lg.shadow-md {
            padding: 6px 4px;
            width: 100%;
            box-sizing: border-box;
          }
          
          /* Giảm khoảng cách giữa các mục */
          .sorting-mode .flex-col.space-y-2,
          .sorting-mode .flex-col.space-y-1 {
            margin-top: 0;
            margin-bottom: 0;
            gap: 3px;
            width: 100%;
          }
          
          /* Giảm kích thước của thông báo */
          .sorting-mode .bg-blue-50.border-l-4 {
            padding: 6px;
            margin-bottom: 6px;
            font-size: 0.7rem;
          }
          
          .sorting-mode .bg-blue-50.border-l-4 .h-6.w-6 {
            height: 14px;
            width: 14px;
          }
          
          .sorting-mode .bg-blue-50.border-l-4 .text-sm {
            font-size: 0.7rem;
          }
          
          /* Giảm kích thước nút lưu */
          .sorting-mode .mt-6 button {
            padding: 0.4rem 0.8rem;
            font-size: 0.8rem;
          }
          
          /* Giảm padding cho container chính */
          .max-w-6xl.mx-auto.px-1.py-1 {
            padding: 0.25rem !important;
          }
          
          /* Đảm bảo text không tràn */
          .truncate {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 100%;
          }
          
          /* Tăng kích thước text cho tên sản phẩm */
          .font-medium.text-gray-800.text-xs {
            font-size: 0.8rem;
            line-height: 1.3;
            margin-bottom: 2px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: normal;
          }
          
          /* Tăng kích thước cho fanpage */
          .bg-blue-50.text-blue-700.text-xs {
            max-width: 200px !important;
            font-size: 0.75rem;
            line-height: 1.2;
            padding: 2px 6px;
          }
          
          /* Giảm kích thước tiêu đề */
          .text-lg {
            font-size: 1rem;
          }
          
          /* Đảm bảo các phần tử flex không tràn */
          .flex {
            max-width: 100%;
          }
          
          /* Đảm bảo các phần tử con không tràn */
          .flex > * {
            min-width: 0;
          }
          
          /* Tăng không gian cho phần thông tin sản phẩm */
          .flex-grow.min-w-0.mr-2 {
            flex: 1 1 auto;
            width: calc(100% - 80px);
            padding-right: 8px;
          }
          
          /* Thu nhỏ phần trạng thái và nút kéo */
          .flex.items-center.flex-shrink-0.ml-auto {
            flex: 0 0 auto;
            width: auto;
            margin-left: 4px;
          }
          
          /* Cho phép text xuống dòng khi cần */
          .truncate {
            white-space: normal;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
          
          /* Tăng khoảng cách giữa các dòng */
          .leading-tight {
            line-height: 1.3;
          }
          
          /* Tối ưu khoảng cách */
          .p-2 {
            padding: 8px 10px;
          }
          
          .gap-1 {
            gap: 4px;
          }
          
          /* Tăng độ tương phản cho text */
          .text-gray-800 {
            color: #1a202c;
          }
          
          .text-blue-700 {
            color: #2b6cb0;
          }
        }
      `;
      document.head.appendChild(style);

      // Ngăn chặn cuộn trang khi đang kéo
      const preventScrollOnDrag = (e) => {
        if (e.target.closest('[data-sortable="true"]')) {
          e.preventDefault();
        }
      };

      document.addEventListener("touchmove", preventScrollOnDrag, {
        passive: false,
      });

      return () => {
        document.body.classList.remove("sorting-mode");
        const styleElement = document.getElementById("drag-cursor-style");
        if (styleElement) {
          styleElement.remove();
        }
        document.removeEventListener("touchmove", preventScrollOnDrag);
      };
    }
  }, [isSortMode]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-4 md:px-8 md:py-8">
        <h1 className="text-lg sm:text-xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-6 text-center">
          Danh Sách Sản Phẩm
        </h1>

        <div className="mb-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 md:flex md:flex-row md:gap-4">
          <div className="sm:col-span-2 md:flex-1">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên sản phẩm hoặc tên fanpage..."
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="md:w-40 relative" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                filterStatus !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span>
                {filterStatus === "all"
                  ? "Trạng thái"
                  : filterStatus === "active"
                  ? "Đang chạy"
                  : "Đã tắt"}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isStatusDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setFilterStatus("all");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Trạng thái
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("active");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đang chạy
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("inactive");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đã tắt
                </button>
              </div>
            )}
          </div>

          <div className="md:w-40 relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                selectedFanpage !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span className="truncate">
                {selectedFanpage === "all"
                  ? "Chọn Fanpage"
                  : fanpages.find((page) => page.id === selectedFanpage)?.name}
              </span>
              <svg
                className={`w-4 h-4 transition-transform flex-shrink-0 ${
                  isDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => handleFanpageFilter("all")}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 truncate"
                >
                  Tất cả Fanpage
                </button>
                {fanpages.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => handleFanpageFilter(page.id)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 truncate"
                    title={page.name}
                  >
                    {page.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={toggleSortMode}
            className={`md:w-40 px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isSortMode
                ? "bg-blue-500 text-white border-blue-500"
                : "bg-white text-gray-700 border-gray-300"
            }`}
          >
            {isSortMode ? "Lưu thứ tự" : "Sắp xếp vị trí"}
          </button>

          {/* Thêm nút chuyển đổi chế độ xem khi không ở chế độ sắp xếp */}
          {/* {!isSortMode && (
            <button
              onClick={() => setViewMode(viewMode === "list" ? "grid" : "list")}
              className="md:w-40 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {viewMode === "list" ? (
                <span className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                  Dạng lưới
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                  Dạng danh sách
                </span>
              )}
            </button>
          )} */}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center">
            <ClipLoader size={50} color={"#123abc"} />
          </div>
        ) : searchAndStatusFilteredProducts.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {products.length === 0
              ? "Không có sản phẩm nào được tạo."
              : "Không tìm thấy kết quả phù hợp với điều kiện tìm kiếm"}
          </div>
        ) : (
          <div>
            {isSortMode ? (
              <div>
                <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-4 mb-4 rounded">
                  <div className="flex items-center">
                    <div className="py-1">
                      <svg
                        className="h-6 w-6 text-blue-500 mr-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold">Chế độ sắp xếp đã được bật</p>
                      <p className="text-sm">
                        Kéo và thả các sản phẩm để thay đổi thứ tự hiển thị.
                        Nhấn "Lưu thứ tự" khi hoàn tất.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-2 sm:p-4">
                  <div className="mb-4 text-sm text-gray-500 hidden md:block">
                    Kéo và thả các sản phẩm để thay đổi thứ tự hiển thị.
                  </div>

                  <div className="mb-3 text-sm text-gray-500 md:hidden">
                    <div className="flex items-center bg-yellow-50 p-2 rounded-lg border border-yellow-200">
                      <svg
                        className="w-4 h-4 text-yellow-500 mr-2 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <span className="text-xs">
                        Nhấn giữ vào sản phẩm và kéo để sắp xếp
                      </span>
                    </div>
                  </div>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    measuring={{
                      droppable: {
                        strategy: "always",
                      },
                    }}
                  >
                    <SortableContext
                      items={searchAndStatusFilteredProducts.map(
                        (item) => item.id
                      )}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {searchAndStatusFilteredProducts.map(
                          (product, index) => (
                            <SortableGridItem
                              key={product.id}
                              product={product}
                              index={index}
                              onProductClick={handleProductClick}
                            />
                          )
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                <div className="mt-6 flex justify-center">
                  <button
                    onClick={toggleSortMode}
                    className="px-6 py-3 bg-blue-500 text-white rounded-lg shadow-md hover:bg-blue-600 transition-colors duration-200 flex items-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                    Lưu thứ tự sắp xếp
                  </button>
                </div>
              </div>
            ) : viewMode === "list" ? (
              <div className="flex flex-col space-y-2">
                {searchAndStatusFilteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg shadow-md p-4 relative transform transition duration-200 ease-in-out hover:shadow-lg cursor-pointer"
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mr-4">
                        {Array.isArray(product.images) &&
                          product.images.length > 0 && (
                            <img
                              src={product.images[0]}
                              alt={`Product ${product.id} thumbnail`}
                              className="w-20 h-20 object-contain bg-gray-100 rounded"
                            />
                          )}
                      </div>
                      <div className="flex-grow min-w-0">
                        <h2 className="text-xl font-bold text-gray-800 mb-1 truncate">
                          {product.name}
                        </h2>
                        {product.fanpages && product.fanpages.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {product.fanpages.map((page, pageIndex) => (
                              <span
                                key={pageIndex}
                                className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded"
                              >
                                {page.name}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="mr-3">
                            {product.images?.length || 0} ảnh
                          </span>
                          <div
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              product.isActive
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {product.isActive ? "Đang chạy" : "Đã tắt"}
                          </div>
                        </div>
                      </div>
                    </div>
                    {product.isPosted && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
                        Đã đăng
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {searchAndStatusFilteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg shadow-md p-4 relative transform transition duration-200 ease-in-out hover:scale-105 hover:shadow-lg cursor-pointer"
                    onClick={() => handleProductClick(product)}
                  >
                    <h2 className="text-2xl font-bold text-gray-800 mb-2 truncate whitespace-nowrap overflow-hidden">
                      {product.name}
                    </h2>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {Array.isArray(product.images) &&
                        product.images
                          .slice(0, 2)
                          .map((image, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={image}
                              alt={`Product ${product.id} Image ${imgIndex}`}
                              className="w-full h-40 object-contain bg-gray-100"
                            />
                          ))}
                    </div>
                    {product.fanpages && product.fanpages.length > 0 && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {product.fanpages.map((page, pageIndex) => (
                            <span
                              key={pageIndex}
                              className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded"
                            >
                              {page.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex justify-between items-center">
                      <div className="text-sm text-gray-600">
                        {product.images?.length || 0} ảnh
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            product.isActive
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {product.isActive ? "🔵 Đang chạy" : "⚪ Đã tắt"}
                        </div>
                      </div>
                    </div>
                    {product.isPosted && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
                        Đã đăng
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Load More Button */}
        {!isLoading && products.length >= visibleCount && (
          <div className="flex justify-center mt-8 mb-8">
            <button
              onClick={() => setVisibleCount((prev) => prev + 50)}
              className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-md font-medium"
            >
              Xem thêm sản phẩm
            </button>
          </div>
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        pauseOnHover={true}
        draggable={true}
      />
    </div>
  );
};

export default ProductsScreen;
